import { Location } from 'vue-router'
import {
  getPrefecture,
  getPrefectureIdBySlug,
  getPrefectureSlugById,
  formatPrefectureName,
  Prefecture,
  PrefectureId,
  prefectureSlugExists,
} from '@/core/Prefectures'
import {
  getActivityIdBySlug,
  activitySlugExists,
  ActivityId,
  Activity,
  getActivitySlugById,
  getActivity,
} from '@/core/Activities'
import { Area, AreasByPref } from '@/externals/gyym/user/areas'
import { Breadcrumb } from '@/tools/breadcrumbsPlugin'
import { getArea } from '@/core/Areas'
import { AreaId, SearchParams } from './types'
import { getCityNameByCode, useFacilitiesMetadataStore } from './actions'
import { isNumeric } from '@/utils/isNumeric'

export const PageTypes = {
  SEARCH: 'search',
  FACILITY_DETAIL: 'facility-detail',
  FACILITY_DETAIL_REVIEW: 'facility-detail-review',
} as const

export const FirstSegmentTypes = {
  SEARCH_PREFECTURE: 'first_search-prefecture',
  SEARCH_ACTIVITY: 'first_search-activity',
  FACILITY_ID: 'first_facility-id',
} as const

export const SecondSegmentTypes = {
  SEARCH_AREA: 'second_search-area',
  SEARCH_CITY_CODE: 'second_search-cityCode',
  SEARCH_ACTIVITY: 'second_search-activity',
  FACILITY_REVIEW: 'second_review',
} as const

export const ThirdSegmentTypes = {
  SEARCH_ACTIVITY: 'third_search-activity',
  FACILITY_ID: 'third_facility-id',
} as const

export type PageType = typeof PageTypes[keyof typeof PageTypes]

export type FirstSegmentType =
  typeof FirstSegmentTypes[keyof typeof FirstSegmentTypes]

export type SecondSegmentType =
  typeof SecondSegmentTypes[keyof typeof SecondSegmentTypes]

export type ThirdSegmentType =
  typeof ThirdSegmentTypes[keyof typeof ThirdSegmentTypes]

export type CityNameResource = {
  cityName: string
}

export type SegmentResource =
  // 都道府県 > エリア
  | {
      data: AreasByPref | Prefecture
      // 都道府県に予約可能施設がない場合はtrue
      // 強制的に検索結果を０件にする
      forceEmptyResult: boolean
    }
  // アクティビティ
  | {
      data: Activity
      // 都道府県に予約可能施設がない場合はtrue
      // 強制的に検索結果を０件にする
      forceEmptyResult: boolean
    }
  // エリア
  | {
      data: Area
      // 都道府県に予約可能施設がない場合はtrue
      // 強制的に検索結果を０件にする
      forceEmptyResult: boolean
    }
  // 市区町村
  | { data: CityNameResource }
  // 無効
  | null
  | undefined

export type ParsedSegments = [
  {
    type: FirstSegmentType | null
    rawValue: string
    value: string | number | null
    resource: SegmentResource
  },
  {
    type: SecondSegmentType | null
    rawValue: string | undefined
    value: string | number | null
    resource: SegmentResource
  },
  {
    type: ThirdSegmentType | null
    rawValue: string | undefined
    value: string | number | null
    resource: SegmentResource
  }
]

const FACILITY_DETAIL_REVIEW_VALUE = 'reviews' as const

const isAreaId = (value: string) =>
  value.startsWith('A') && isNumeric(value.substr(1))

const isCityCode = (value: string) => {
  if (!value.startsWith('C')) return false
  const cityCode = value.substr(1)
  // 現状、市区町村コードがint型の都合で先頭の0を省くため、4桁or5桁
  return isNumeric(cityCode) && (cityCode.length === 4 || cityCode.length === 5)
}

export const getFirstSegmentType = (value: string): FirstSegmentType | null => {
  if (isNumeric(value)) {
    return FirstSegmentTypes.FACILITY_ID
  }

  if (prefectureSlugExists(value)) {
    return FirstSegmentTypes.SEARCH_PREFECTURE
  }

  if (activitySlugExists(value)) {
    return FirstSegmentTypes.SEARCH_ACTIVITY
  }

  return null
}

export const getSecondSegmentType = (
  value: string
): SecondSegmentType | null => {
  if (value === FACILITY_DETAIL_REVIEW_VALUE) {
    return SecondSegmentTypes.FACILITY_REVIEW
  }

  if (isAreaId(value)) {
    return SecondSegmentTypes.SEARCH_AREA
  }

  if (isCityCode(value)) {
    return SecondSegmentTypes.SEARCH_CITY_CODE
  }

  if (activitySlugExists(value)) {
    return SecondSegmentTypes.SEARCH_ACTIVITY
  }

  return null
}

export const getThirdSegmentType = (value: string): ThirdSegmentType | null => {
  if (isNumeric(value)) {
    return ThirdSegmentTypes.FACILITY_ID
  }

  if (activitySlugExists(value)) {
    return ThirdSegmentTypes.SEARCH_ACTIVITY
  }

  return null
}

/**
 * セグメントの値を正しい型にフォーマットしつつキャストする
 */
export const castSegmentValue = (value?: string): string | number | null => {
  if (!value) return null

  if (isAreaId(value) || isCityCode(value)) {
    return Number(value.substr(1))
  }

  if (activitySlugExists(value)) {
    return getActivityIdBySlug(value)
  }

  if (prefectureSlugExists(value)) {
    return getPrefectureIdBySlug(value)
  }

  if (isNumeric(value)) {
    return Number(value)
  }

  return value
}

/**
 * セグメントの値から対応するリソースを取得する
 */
export const getResourceBySegmentValue = async (
  value?: string
): Promise<SegmentResource> => {
  const store = useFacilitiesMetadataStore()

  await store.fetch()

  if (!value) return null

  if (isAreaId(value)) {
    const areaId = castSegmentValue(value) as AreaId
    const reservableArea = store.getArea(areaId)
    const data = reservableArea ?? (await getArea(areaId))
    return { data, forceEmptyResult: !reservableArea }
  }

  if (isCityCode(value)) {
    const cityCode = castSegmentValue(value) as number
    const city = await getCityNameByCode(cityCode)
    return city.cityName ? { data: city } : null
  }

  if (activitySlugExists(value)) {
    const activityId = castSegmentValue(value) as ActivityId
    const reservableActivity = store.getActivity(activityId)
    const data = reservableActivity ?? (await getActivity(activityId))
    return { data, forceEmptyResult: !reservableActivity }
  }

  if (prefectureSlugExists(value)) {
    const prefectureId = castSegmentValue(value) as PrefectureId
    const reservablePrefecture = store.getPrefecture(prefectureId)
    const data = reservablePrefecture ?? (await getPrefecture(prefectureId))
    return data
      ? {
          data,
          forceEmptyResult: !reservablePrefecture,
        }
      : null
  }

  return null
}

/**
 * セグメント数とセグメントタイプからページタイプを取得する
 */
const detectPageType = (
  segmentDepth: 1 | 2 | 3 | null,
  segmentTypes: [
    FirstSegmentType | null,
    SecondSegmentType | null,
    ThirdSegmentType | null
  ]
): PageType | null => {
  const [firstSegmentType, secondSegmentType, thirdSegmentType] = segmentTypes

  switch (segmentDepth) {
    case 1:
      return firstSegmentType
        ? firstSegmentType === FirstSegmentTypes.FACILITY_ID
          ? PageTypes.FACILITY_DETAIL
          : PageTypes.SEARCH
        : null
    case 2: {
      if (!firstSegmentType || !secondSegmentType) return null

      // 最初のセグメントが施設IDの場合
      if (firstSegmentType === FirstSegmentTypes.FACILITY_ID) {
        return secondSegmentType === SecondSegmentTypes.FACILITY_REVIEW
          ? // 第2セグメントはreviewのみ許容する
            PageTypes.FACILITY_DETAIL_REVIEW
          : // それ以外は無効
            null
      }

      // 最初のセグメントがアクティビティの場合、第2セグメントはなし
      if (firstSegmentType === FirstSegmentTypes.SEARCH_ACTIVITY) return null

      // それ以外は検索ページ
      return PageTypes.SEARCH
    }
    case 3: {
      if (!firstSegmentType || !secondSegmentType || !thirdSegmentType)
        return null

      // 最初のセグメントが施設IDの場合、第2セグメント以降はなし
      if (firstSegmentType === FirstSegmentTypes.FACILITY_ID) return null

      // 最初か2つ目のセグメントがアクティビティの場合、第3セグメントはなし
      if (firstSegmentType === FirstSegmentTypes.SEARCH_ACTIVITY) return null
      if (secondSegmentType === SecondSegmentTypes.SEARCH_ACTIVITY) return null

      // {prefecture}/{cityCode}/{facilityId} のときは施設詳細
      if (
        firstSegmentType === FirstSegmentTypes.SEARCH_PREFECTURE &&
        secondSegmentType === SecondSegmentTypes.SEARCH_CITY_CODE &&
        thirdSegmentType === ThirdSegmentTypes.FACILITY_ID
      ) {
        return PageTypes.FACILITY_DETAIL
      }

      // それ以外は検索ページ
      return PageTypes.SEARCH
    }
    default:
      return null
  }
}

/**
 * セグメントのバリデーション
 */
const isSegmentTypesValid = ([firstSegment, secondSegment]: // thirdSegment,
ParsedSegments): boolean => {
  // エリア検索の場合、一つ前のセグメントの都道府県に属してるエリアでなければならない
  if (secondSegment.type === SecondSegmentTypes.SEARCH_AREA) {
    // 最初のセグメントから都道府県情報を取得、検証
    const areas = (firstSegment.resource?.data as any)?.areas ?? []
    return areas.some((area: any) => area.id === secondSegment.value)
  }

  return true
}

/**
 * /facilities以下のURLの最大3セグメントをパースする
 */
export const parseSegments = async ([first, second, third]:
  | [string]
  | [string, string]
  | [string, string, string]): Promise<{
  isValid: boolean
  isResourceAvailable: boolean
  forceEmptyResult: boolean
  pageType: PageType | null
  depth: 1 | 2 | 3 | null
  types: ParsedSegments
}> => {
  const segmentDepth = third ? 3 : second ? 2 : first ? 1 : null

  const firstSegment = {
    type: getFirstSegmentType(first),
    rawValue: first,
    value: castSegmentValue(first),
    resource: await getResourceBySegmentValue(first),
  }
  const secondSegment = {
    type: second ? getSecondSegmentType(second) : null,
    rawValue: second,
    value: castSegmentValue(second),
    resource: await getResourceBySegmentValue(second),
  }
  const thirdSegment = {
    type: third ? getThirdSegmentType(third) : null,
    rawValue: third,
    value: castSegmentValue(third),
    resource: await getResourceBySegmentValue(third),
  }

  const pageType = detectPageType(segmentDepth, [
    firstSegment.type,
    secondSegment.type,
    thirdSegment.type,
  ])

  const isResourceAvailable =
    segmentDepth === 1
      ? Boolean(firstSegment.resource)
      : segmentDepth === 2
      ? Boolean(firstSegment.resource && secondSegment.resource)
      : Boolean(
          firstSegment.resource &&
            secondSegment.resource &&
            thirdSegment.resource
        )

  const types: ParsedSegments = [firstSegment, secondSegment, thirdSegment]
  const isValid = isSegmentTypesValid(types)

  return {
    isValid: isValid && !!pageType,
    isResourceAvailable,
    forceEmptyResult: types.some((t) => (t.resource as any)?.forceEmptyResult),
    pageType,
    depth: segmentDepth,
    types,
  }
}

/**
 * セグメントの情報を元に検索条件を作成する
 */
export const buildConditions = ([first, second, third]: ParsedSegments):
  | Partial<SearchParams>
  | never => {
  const params = {} as Partial<SearchParams>

  if (first.type && first.value && !(first.resource as any)?.forceEmptyResult) {
    switch (first.type) {
      case FirstSegmentTypes.SEARCH_PREFECTURE: {
        const pref = first.resource?.data as
          | AreasByPref
          | Prefecture
          | undefined
        params['areaIds'] =
          pref && (pref as AreasByPref).areas
            ? (pref as AreasByPref).areas.map((area) => area.id)
            : []
        break
      }
      case FirstSegmentTypes.SEARCH_ACTIVITY:
        params['activityIds'] = [first.value as number]
        break
      case FirstSegmentTypes.FACILITY_ID:
        // Should not be called
        throw new Error(
          'Trying to build conditions inside facility detail page'
        )
    }
  }

  if (
    second.type &&
    second.value &&
    !(second.resource as any)?.forceEmptyResult
  ) {
    switch (second.type) {
      case SecondSegmentTypes.SEARCH_AREA:
        params['areaIds'] = [second.value as number]
        break
      case SecondSegmentTypes.SEARCH_CITY_CODE:
        params['cityCode'] = second.value as number
        break
      case SecondSegmentTypes.SEARCH_ACTIVITY:
        params['activityIds'] = [second.value as number]
        break
      case SecondSegmentTypes.FACILITY_REVIEW:
        // Should not be called
        throw new Error(
          'Trying to build conditions inside facility detail review page'
        )
    }
  }

  if (third.type && third.value && !(third.resource as any)?.forceEmptyResult) {
    switch (third.type) {
      case ThirdSegmentTypes.SEARCH_ACTIVITY:
        params['activityIds'] = [third.value as number]
        break
      case ThirdSegmentTypes.FACILITY_ID:
        // Should not be called
        throw new Error(
          'Trying to build conditions inside facility detail page'
        )
    }
  }

  return params
}

/**
 * 検索条件からパスを生成する
 */
export const conditionsToPath = (
  params: Partial<SearchParams>,
  areasByPref: AreasByPref[],
  sortValue?: { sortBy: string; order: 'asc' | 'desc' },
  isMapView: boolean = false
): Location => {
  let query = Object.entries(params).reduce(
    (acc, [key, value]) => {
      // falsyの場合はクエリに含めない
      if (!value) return acc

      // 空配列の場合はクエリに含めない
      if (Array.isArray(value) && value.length === 0) return acc

      return { ...acc, [key]: value }
    },
    {
      ...(isMapView ? { view: 'map' } : {}),
      ...(sortValue?.sortBy ? { sortBy: sortValue.sortBy } : {}),
    } as Partial<SearchParams>
  )

  let path: string = '/facilities'

  if (query.areaIds) {
    const { areaIds, ...rest } = query

    if (areaIds.length === 1) {
      const pref = areasByPref.find((p) =>
        Boolean(p.areas.find((a) => a.id === areaIds[0]))
      )

      if (pref) {
        if (pref.areas.length === areaIds.length) {
          // エリアが1つ選択されている場合で、
          // 該当の都道府県が１つしかエリアを持っていない場合は /{prefecture}
          path += `/${getPrefectureSlugById(pref.id)}`
        } else {
          // エリアが1つ選択されている場合は /{prefecture}/A{area_id}
          path += `/${getPrefectureSlugById(pref.id)}/A${areaIds[0]}`
        }

        // クエリからareaIdsを除外する
        query = rest
      }
    } else {
      // エリアが一つ以上選択されている場合
      const pref = areasByPref.find((p) =>
        p.areas.every((a) => areaIds.includes(a.id))
      )
      if (pref && pref.areas.length === areaIds.length) {
        path = `${path}/${getPrefectureSlugById(pref.id)}`

        // クエリからareaIdsを除外する
        query = rest
      }
    }
  }

  // アクティビティが1つ選択されている場合は末尾に /{activity} を追加
  if (query.activityIds && query.activityIds.length === 1) {
    const { activityIds, ...rest } = query
    path = `${path}/${getActivitySlugById(Number(activityIds[0]))}`

    // クエリからactivityIdsを削除する
    query = rest
  }

  // 検索条件がない場合は /facilities
  return {
    path,
    query: query as Location['query'],
  }
}

/**
 * セグメントの情報からページ名に変換する
 */
const pageSegmentToName = async (
  segment: ParsedSegments[number]
): Promise<string | null> => {
  if (!segment.resource) return null

  switch (segment.type) {
    case FirstSegmentTypes.SEARCH_PREFECTURE: {
      const pref = segment.resource.data as Prefecture | AreasByPref
      return formatPrefectureName(pref.name)
    }
    case SecondSegmentTypes.SEARCH_AREA:
      return (segment.resource.data as Area)!.name
    case SecondSegmentTypes.SEARCH_CITY_CODE:
      return (segment.resource.data as CityNameResource)!.cityName
    case FirstSegmentTypes.SEARCH_ACTIVITY:
    case SecondSegmentTypes.SEARCH_ACTIVITY:
    case ThirdSegmentTypes.SEARCH_ACTIVITY:
      return (segment.resource.data as Activity)!.name
    default:
      return null
  }
}

/**
 * セグメントを読める形に変換する
 */
export const pageSegmentsToNames = async (
  segments: ParsedSegments
): Promise<string[]> => {
  const validSegments = segments.filter(({ type }) => type !== null)

  if (validSegments.length === 0) return []

  const names = await Promise.all(validSegments.map(pageSegmentToName))
  return names.filter((n) => n !== null) as string[]
}

/**
 * セグメントをページタイトルに変換する
 */
export const pageSegmentsToTitle = async (
  segments: ParsedSegments,
  defaultTitle: string = '施設検索'
): Promise<string> => {
  const validSegments = segments.filter(({ type }) => type !== null)
  if (validSegments.length === 0) return defaultTitle

  const names = await pageSegmentsToNames(segments)

  // 1つ以上のセグメントがあり、最初のセグメントが都道府県の場合は都道府県名を省略する
  if (
    validSegments.length >= 2 &&
    validSegments[0].type === FirstSegmentTypes.SEARCH_PREFECTURE &&
    // ただし、2つ目のセグメントがアクティビティの場合は除く
    validSegments[1].type !== SecondSegmentTypes.SEARCH_ACTIVITY
  ) {
    names.shift()
  }

  return names.length === 0 ? defaultTitle : names.join(' ')
}

/**
 * セグメントの情報からパンくずに変換する
 */
const pageSegmentToBreadcrumb = async (
  segment: ParsedSegments[number],
  pathPrefix: string = '/'
): Promise<Breadcrumb | null> => {
  switch (segment.type) {
    case FirstSegmentTypes.SEARCH_ACTIVITY:
    case FirstSegmentTypes.SEARCH_PREFECTURE:
    case SecondSegmentTypes.SEARCH_ACTIVITY:
    case SecondSegmentTypes.SEARCH_CITY_CODE:
    case SecondSegmentTypes.SEARCH_AREA:
    case ThirdSegmentTypes.SEARCH_ACTIVITY:
      return {
        name: (await pageSegmentToName(segment))!,
        path: `${pathPrefix ? pathPrefix : '/'}/${segment.rawValue}`,
      }
    default:
      return null
  }
}

/**
 * セグメントをパンくずに変換する
 */
export const pageSegmentsToBreadcrumbs = async (
  segments: ParsedSegments,
  initialBreadcrumb: Breadcrumb = { name: '施設検索', path: '/facilities' }
): Promise<Breadcrumb[]> => {
  const validSegments = segments.filter(({ type }) => type !== null)
  if (validSegments.length === 0) return [initialBreadcrumb]

  return segments.reduce<Promise<Breadcrumb[]>>(async (acc, segment, i) => {
    const prevResult = await acc

    const breadcrumb = await pageSegmentToBreadcrumb(
      segment,
      (prevResult[i] && prevResult[i].path) || ''
    )

    // パンくずが無効な場合は追加しない
    if (!breadcrumb) return prevResult

    // 最後のセグメントのpathは除く (active状態にする)
    if (validSegments.length - 1 === i) {
      return [
        ...prevResult,
        {
          name: (await pageSegmentToName(segment))!,
        },
      ]
    }

    return [...prevResult, breadcrumb]
  }, Promise.resolve([initialBreadcrumb]))
}

/**
 * セグメントからページのメタデータを生成する
 */
export const getPageMetadataFromSegments = async (segments: ParsedSegments) => {
  const [title, breadcrumbs] = await Promise.all([
    pageSegmentsToTitle(segments),
    pageSegmentsToBreadcrumbs(segments),
  ])

  // エリアは `・`、都道府県やアクティビティ等は半角スペースでsplit
  const keywords = title.split(/\s|・/g)

  return { title, keywords, breadcrumbs }
}
